<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid create-job-container p-b-40">
        <v-card class="p-b-30 p-t-30 p-l-20">
          <div class="el-loading-mask" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Card Title -->
          <div class="row">
            <div class="btn-actions-pane-left">
              <h3 class="title-1 p-l-140">
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click="$router.back()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                <span v-if="!isEditing">
                  {{ text('create_live_stream') }}
                </span>
                <span v-else>
                  {{ text('edit_live_stream') }} <span class="p-l-20 status--gray"> ID={{ form.id }}</span>
                </span>
              </h3>
            </div>
          </div>
          <!-- Name and Profile -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10 section-description">
                {{ text('live_stream_name_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-4">
                <v-text-field
                  v-model="form.name"
                  :label="text('title_label')"
                  :counter="255"
                  :rules="rules.nameRules"
                  required>
                </v-text-field>
              </div>
              <div class="col-md-6 p-r-0">
                <v-select
                  :disabled="isEditing"
                  prepend-icon="fas fa-sliders-h"
                  v-model="form.profile"
                  :label="text('profile_label')"
                  item-text="name"
                  item-value="id"
                  :append-icon="form.profile ? 'zoom_in' : ''"
                  @click:append="profileInfoDialog=true"
                  :items="profiles">
                </v-select>
              </div>
            </div>
          </div>
          <!-- INPUT -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l">
                <h2>{{ text('input') }}</h2>
              </div>
              <div class="col-md-10 section-description">
                {{ text('live_stream_input_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10 p-r-0">
                <div class="col-md-4 p-l-0 input-output-type" style="max-width: 200px;">
                  <v-select
                    hide-details
                    v-model="form.input.type"
                    :label="text('type_label')"
                    :items="inputTypes"
                    @change="onInputTypeChange">
                  </v-select>
                </div>
                <div class="col-md-12 p-l-0 p-r-0 p-t-20"
                     v-if="!['rtmp push', 'astra'].includes(form.input.type)">
                  <v-text-field
                    hide-details
                    outline
                    v-model="form.input.url.url"
                    @change="onInputURLChange"
                    label="URL*"
                    :counter="1024"
                    :placeholder="form.input.url.selectedType + text('host_path_hint')"
                    :append-outer-icon="form.input.url.showDetails ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                    @click:append-outer="form.input.url.showDetails = !form.input.url.showDetails"
                    :error="form.input.url.url !== '' && !form.input.url.hostPath">
                  </v-text-field>
                  <v-expand-transition>
                    <div class="url-details" v-show="form.input.url.showDetails">
                      <div class="row">
                        <div class="col-md-5">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.login"
                            :label="text('login')">
                          </v-text-field>
                        </div>
                        <div class="col-md-7">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.password"
                            :label="form.input.url.selectedType === 'azure' ? text('shared_key') : text('password')">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-md-12">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.hostPath"
                            :label="text('host_path')">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row p-t-20"
                           v-show="['http', 'http+hls'].includes(form.input.url.selectedType)">
                        <div class="col-md-12 secure-setting">
                          <v-checkbox
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.secure">
                          </v-checkbox>
                          <span @click="changeInputSecure" class="custom-selector-label p-l-20">
                            {{ text('secure') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
                <div class="col-md-12 p-l-0 p-r-0 p-t-20"
                     v-if="['http', 'http+hls', 'astra', 'srt'].includes(form.input.type)">
                  <v-btn
                    depressed
                    small
                    text
                    class="m-0"
                    @click="showAdvancedSettings = !showAdvancedSettings">
                    <div class="overline" v-if="showAdvancedSettings">{{ text('advance_settings_close') }}</div>
                    <div class="overline" v-else>{{ text('advance_settings_open') }}</div>
                  </v-btn>
                </div>
                <div class="col-md-12 p-l-0 p-r-0 p-t-5"
                     v-if="['http', 'http+hls', 'astra', 'srt'].includes(form.input.type) && showAdvancedSettings">
                  <div class="row">
                    <div class="col-md-4 p-r-0" style="max-width: 140px;">
                      <v-text-field
                        type="number"
                        v-model="form.input.pn"
                        hint="e.g. 10805"
                        label="Program Number">
                      </v-text-field>
                    </div>
                    <div class="col-md-4 p-r-0" style="max-width: 200px;">
                      <v-text-field
                        v-model="form.input.video_pid"
                        hint="e.g. 1805"
                        persistent-hint
                        label="Video PID">
                      </v-text-field>
                    </div>
                    <div class="col-md-4 p-r-0" style="max-width: 200px;">
                      <v-text-field
                        hide-detais
                        v-model="form.input.audio_pid"
                        hint="e.g. 1905,1906"
                        persistent-hint
                        label="Audio PID(s)">
                      </v-text-field>
                    </div>
                    <div class="col-md-4 p-r-0" style="max-width: 200px;">
                      <v-text-field
                        v-model="form.input.data_pid"
                        hint="e.g. 715,716"
                        persistent-hint
                        label="Data PID(s)">
                      </v-text-field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- OUTPUTS -->
          <div class="section">
            <div class="row p-b-5 p-t-20">
              <div class="col-md-2 col-l">
                <h2>{{ text('output_header') }}</h2>
              </div>
              <div class="col-md-10 section-description">
                {{ text('live_output_text') }}
              </div>
            </div>
            <div class="row" v-for="(output, i) of form.outputs" :key="i"
                 :class="{
                 'p-t-20': i > 0,
                 'p-b-20': form.outputs.length > 1 && i < form.outputs.length-1,
                 }">
              <div class="col-md-2 col-l">
                <p class="p-t-15">
                  #{{ i+1 }} {{ text('of') }} {{ form.outputs.length }}<br/>
                  <a v-if="form.outputs.length > 1"
                     class="remove-output"
                     @click="removeOutput(i)">
                    <i class="zmdi zmdi-minus remove-icon p-r-5"></i>
                    {{ text('remove_btn') }}
                  </a>
                </p>
              </div>
              <div class="col-md-10 p-r-0 p-t-5">
                <v-divider></v-divider>
                <div class="col-md-4 p-l-0 input-output-type" style="max-width: 200px;">
                  <v-select
                    hide-details
                    v-model="output.type"
                    :label="text('type_label')"
                    :items="outputTypes"
                    @change="onOutputTypeChange(i)">
                  </v-select>
                </div>
                <div class="col-md-12 p-l-0 p-r-0 p-t-20" v-if="output.type === 'webdav'">
                  <v-text-field
                    hide-details
                    outline
                    v-model="output.url.url"
                    @change="onOutputURLChange(i)"
                    :placeholder="output.url.selectedType + text('host_path_hint')"
                    :label="text('destination_label') + '*'"
                    :append-outer-icon="output.url.showDetails ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                    @click:append-outer="output.url.showDetails = !output.url.showDetails"
                    :error="output.url.url !== '' && !output.url.hostPath && output.url.selectedType !== 'origin'">
                  </v-text-field>
                  <v-expand-transition>
                    <div class="url-details" v-show="output.url.showDetails">
                      <div class="row">
                        <div class="col-md-5">
                          <v-text-field
                            hide-details
                            @change="buildOutputUrl(i)"
                            v-model="output.url.login"
                            :label="text('login')">
                          </v-text-field>
                        </div>
                        <div class="col-md-7">
                          <v-text-field
                            hide-details
                            @change="buildOutputUrl(i)"
                            v-model="output.url.password"
                            :label="output.url.selectedType === 'azure' ? text('shared_key') : text('password')">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row p-b-10">
                        <div class="col-md-12">
                          <v-text-field
                            hide-details
                            @change="buildOutputUrl(i)"
                            v-model="output.url.hostPath"
                            :label="text('host_path')">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row p-b-10"
                           v-show="['http'].includes(output.url.selectedType)">
                        <div class="col-md-12 secure-setting">
                          <v-checkbox
                            hide-details
                            @change="buildOutputUrl(i)"
                            v-model="output.url.secure">
                          </v-checkbox>
                          <span @click="changeOutputSecure(i)" class="custom-selector-label p-l-20">
                            {{ text('secure') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
                <div class="row p-t-15">
                  <div class="col-md-4 p-r-0" style="max-width: 200px;">
                    <v-select
                      v-model="output.format"
                      :label="text('format_label')"
                      item-text="title"
                      item-value="extId"
                      :items="outputFormats(i)">
                    </v-select>
                  </div>
                  <div class="col-md-4 p-r-0" style="max-width: 150px;"
                       v-if="output.format === 'segmented_hls'">
                    <v-select
                      :items="hlsVersions"
                      :label="text('version_label')"
                      v-model="output.hlsVersion">
                    </v-select>
                  </div>
                  <div class="col-md-4 p-r-0" style="max-width: 150px;"
                       v-if="['segmented_hls', 'segmented_dash', 'segmented_ss'].includes(output.format)">
                    <v-text-field
                      type="number"
                      :label="text('segment_size_label')"
                      v-model="output.segmentSize"
                      :rules="rules.segmentSize"
                      :suffix="text('sec')">
                    </v-text-field>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Add output button -->
          <div class="row p-l-45 add-output">
            <i class="zmdi zmdi-plus p-r-5"></i>
            <a @click="addOutput()">{{ text('add_output_btn') }}</a>
          </div>
          <!-- Action button -->
          <div class="row text-right p-t-10">
            <div class="col-md-2 col-l"></div>
            <div class="col-md-10">
              <v-btn
                large
                :loading="isSaving"
                :disabled="isSaving || !isJobValid"
                color="success"
                @click="saveJob">
                <span v-if="isEditing">{{ text('save_stream_btn') }}</span>
                <span v-else>{{ text('create_btn') }}</span>
              </v-btn>
            </div>
          </div>
          <!-- Profile Info Dialog -->
          <v-dialog v-model="profileInfoDialog" max-width="900">
            <v-card class="p-b-15">
              <v-card-text v-if="selectedProfile.name">
                <div class="row">
                  <div class="col-md-12">
                    <v-btn
                      icon
                      @click="profileInfoDialog = false">
                      <v-icon>zmdi-close</v-icon>
                    </v-btn>
                    <span class="headline align-middle">{{ selectedProfile.name }}</span>
                  </div>
                </div>
                <v-divider/>
                <table class="profile-detail-table">
                  <tbody>
                  <tr>
                    <td><span class="card-item-title">ID</span></td>
                    <td>{{ selectedProfile.id }}</td>
                  </tr>
                  <tr>
                    <td><span class="card-item-title">Audio codec</span></td>
                    <td class="text-uppercase">{{ selectedProfile.audio_template.codec }}</td>
                  </tr>
                  <tr>
                    <td><span class="card-item-title">Video codec</span></td>
                    <td>
                      <span class="text-uppercase" v-if="selectedProfile.video_template.codec">
                        {{ videoCodecMap[selectedProfile.video_template.codec][0] }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><span class="card-item-title">Video streams</span></td>
                    <td>{{ selectedProfile.video_template.streams.length }}</td>
                  </tr>
                  <tr v-if="selectedProfile.video_template.codec.startsWith('h26')">
                    <td><span class="card-item-title">Encoder</span></td>
                    <td>
                      <span v-if="selectedProfile.video_template.codec">
                        {{ videoCodecMap[selectedProfile.video_template.codec][1]
                        || videoCodecMap[selectedProfile.video_template.codec][2] }}
                      </span>
                    </td>
                  </tr>
                  </tbody>
                </table>
                <v-divider/>
                <table class="profile-detail-table">
                  <tbody>
                  <tr>
                    <td><span class="card-item-title">Audio channels</span></td>
                    <td>
                      {{ audioChannelsMap[selectedProfile.audio_template.channels]
                      || 'Same as source' }}
                    </td>
                  </tr>
                  <tr>
                    <td><span class="card-item-title">Audio bitrate</span></td>
                    <td>{{ selectedProfile.audio_template.bitrate }} Kbps</td>
                  </tr>
                  <tr>
                    <td><span class="card-item-title">Sample rate</span></td>
                    <td>
                      <span v-if="selectedProfile.audio_template.sample_rate">
                        {{ selectedProfile.audio_template.sample_rate }} Hz
                      </span>
                      <span v-else>Same as source</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
                <template v-if="selectedProfile.video_template.streams.length">
                  <table class="profile-streams m-t-15">
                    <thead>
                    <tr>
                      <th class="number-column">Video stream</th>
                      <th v-if="selectedProfile.video_template.codec.startsWith('h26')">
                        Profile
                      </th>
                      <th v-if="selectedProfile.video_template.codec.startsWith('h26')">Level</th>
                      <th>Resolution</th>
                      <th>Bitrate, Kbps</th>
                      <th>Resize Mode</th>
                      <th>Frame Rate, Fps</th>
                      <th>KeyInt / MinKeyInt</th>
                      <th v-if="selectedProfile.video_template.codec.startsWith('h26')">
                        Ref Frames / BFrames
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(stream, i) of selectedProfile.video_template.streams" :key="i">
                      <td>#{{ i+1 }}</td>
                      <td v-if="selectedProfile.video_template.codec.startsWith('h264')">
                        {{ h264ProfileMap[stream.h264_codec_profile] }}
                      </td>
                      <td v-if="selectedProfile.video_template.codec.startsWith('h265')">
                        {{ h265ProfileMap[stream.h264_codec_profile] }}
                      </td>
                      <td v-if="selectedProfile.video_template.codec.startsWith('h264')">
                        {{ stream.h264_codec_level || 'Auto' }}
                      </td>
                      <td v-if="selectedProfile.video_template.codec.startsWith('h265')">
                        {{ stream.h264_codec_level || 'Auto' }}
                      </td>
                      <td>{{ stream.resolution || 'Same as source'}}</td>
                      <td>{{ stream.bitrate }}</td>
                      <td>{{ resizeModeMap[stream.aspect_ratio] || 'Same as source' }}</td>
                      <td>
                        <span v-if="stream.target_fps">{{ stream.target_fps }}</span>
                        <span v-else>Same as source</span>
                      <td>{{ stream.keyframe_int || '0' }}/{{ stream.min_keyframe_int || '0' }}</td>
                      <td v-if="selectedProfile.video_template.codec.startsWith('h26')">
                        {{ stream.h264_reference_frames || '0' }}/{{ stream.h264_bframes || '0' }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </template>
              </v-card-text>
            </v-card>
          </v-dialog>
          <!-- -->
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { joinURL, parseURL } from '@/helpers/utils';
import {
  AUDIO_CHANNELS_MAP,
  H264_CODEC_PROFILE_MAP,
  H265_CODEC_PROFILE_MAP,
  LIVE_ENCODING_FORMATS,
  RESIZE_MODE_MAP,
  VIDEO_CODEC_MAP,
} from '@/components/transcoding/shared/types';
import { processURLInfo } from '@/components/transcoding/shared/url';

const urlObject = {
  url: '',
  selectedType: 'rtmp',
  login: '',
  password: '',
  hostPath: '',
  secure: false, // HTTP, FTP, HTTP+HLS
  showDetails: false,
};

const outputObject = {
  format: 'segmented_hls',
  url: {}, // urlObject
  type: 'origin',
  segmentSize: 4,
  hlsVersion: 3,
};

const inputSupportedSchemes = {
  http: true,
  https: true,
  rtmp: true,
  'http+hls': true,
  'https+hls': true,
  srt: true,
};

const inputSchemeToType = {
  http: 'http',
  rtmp: 'rtmp pull',
  'http+hls': 'http+hls',
  srt: 'srt',
};

const inputTypeToScheme = {
  http: 'http',
  'rtmp pull': 'rtmp',
  'http+hls': 'http+hls',
  srt: 'srt',
};

const outputSupportedSchemes = {
  http: true,
  https: true,
};

const outputTypeToScheme = {
  webdav: 'http',
};

function buildURL(urlObj) {
  let scheme = urlObj.selectedType;
  if (scheme === 'http' && urlObj.secure) {
    scheme = 'https';
  }
  if (scheme === 'http+hls' && urlObj.secure) {
    scheme = 'https+hls';
  }
  let lp = '';
  if (urlObj.login) {
    lp = urlObj.password ? `${urlObj.login}:${urlObj.password}@` : `${urlObj.login}@`;
  }
  const { hostPath } = urlObj;
  return `${scheme}://${lp}${hostPath}`;
}

function NormOutputToJsonAPI(output) {
  const convertedOutput = {
    format: output.format,
    url: [],
    id: output.id ? output.id : 0,
  };
  if (output.format === 'segmented_hls') {
    convertedOutput.hls_version = Number(output.hlsVersion);
  }
  if (output.format.startsWith('segmented')) {
    convertedOutput.segment_size = Number(output.segmentSize);
  }
  if (output.type === 'webdav') {
    convertedOutput.url.push(output.url.url);
  } else if (output.type === 'origin') {
    convertedOutput.url.push('origin://');
  }
  return convertedOutput;
}

function JsonAPIOutputToNorm(output) {
  const convertedOutput = {
    id: output.id,
    format: output.format,
    url: {},
    type: '',
    segmentSize: Number(output.segment_size) || 4,
    hlsVersion: Number(output.hls_version) || 3,
  };
  if (output.url.length === 0) {
    convertedOutput.url = Vue.util.extend({}, urlObject);
    convertedOutput.url.selectedType = 'http';
    convertedOutput.type = 'origin';
  } else if (output.url[0].startsWith('origin')) {
    convertedOutput.type = 'origin';
    convertedOutput.url = Vue.util.extend({}, urlObject);
  } else {
    convertedOutput.type = 'webdav';
    const newURLObj = processURLInfo({ url: output.url[0] }, outputSupportedSchemes, 'http');
    convertedOutput.url = Vue.util.extend({}, newURLObj);
  }
  return convertedOutput;
}

function NormJobToJsonAPI(job) {
  const t = +new Date();
  const convertedJob = {
    id: job.id,
    ext_id: `${t}:${job.name}`,
    type: 'live',
    name: job.name,
    profile: {
      id: Number(job.profile),
    },
    input: {},
    outputs: [],
  };
  // input
  if (job.input.type === 'rtmp push') {
    convertedJob.input.url = 'rtmp://';
  } else if (job.input.type === 'astra') {
    convertedJob.input.url = 'astra://';
  } else {
    convertedJob.input.url = job.input.url.url;
  }

  if (job.input.pn || job.input.audio_pid || job.input.video_pid || job.input.data_pid) {
    const parsed = parseURL(convertedJob.input.url);
    const audio = job.input.audio_pid.split(',').map(v => v.trim()).join(',');
    const data = job.input.data_pid.split(',').map(v => v.trim()).join(',');
    const query = [];

    const queryArgs = {};
    const vars = parsed.query.split('&');
    for (let i = 0; i < vars.length; i += 1) {
      const pair = vars[i].split('=');
      // eslint-disable-next-line no-continue
      if (pair.length !== 2) continue;
      queryArgs[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
    }

    // eslint-disable-next-line prefer-destructuring
    const pn = queryArgs.pn;
    if (job.input.pn && job.input.pn !== '0') {
      query.push(`pn=${job.input.pn}`);
      delete queryArgs.pn;
    } else if (pn) {
      query.push(`pn=${pn}`);
      delete queryArgs.pn;
    }
    const existingAudio = queryArgs.audio_pid;
    if (audio && audio !== '0') {
      query.push(`audio_pid=${audio}`);
      delete queryArgs.audio_pid;
    } else if (existingAudio) {
      query.push(`audio_pid=${existingAudio}`);
      delete queryArgs.audio_pid;
    }
    const existingVideo = queryArgs.video_pid;
    if (job.input.video_pid && job.input.video_pid !== '0') {
      query.push(`video_pid=${job.input.video_pid}`);
      delete queryArgs.video_pid;
    } else if (existingVideo) {
      query.push(`video_pid=${existingVideo}`);
      delete queryArgs.video_pid;
    }
    const existingData = queryArgs.data_pid;
    if (data && data !== '0') {
      query.push(`data_pid=${data}`);
      delete queryArgs.data_pid;
    } else if (existingData) {
      query.push(`data_pid=${existingData}`);
      delete queryArgs.data_pid;
    }
    if (query.length > 0) {
      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const key in queryArgs) {
        query.push(`${key}=${queryArgs[key]}`);
      }
      parsed.query = query.join('&');
      convertedJob.input.url = joinURL(parsed);
    }
  }

  // output
  for (let i = 0; i < job.outputs.length; i += 1) {
    convertedJob.outputs.push(NormOutputToJsonAPI(job.outputs[i]));
  }
  return convertedJob;
}

function JsonAPIJobToNorm(job) {
  const convertedJob = {
    id: job.id,
    name: job.name,
    profile: Number(job.profile.id),
    input: {
      url: {},
      type: 'rtmp pull',
      pn: '0',
      audio_pid: '0',
      video_pid: '0',
      data_pid: '0',
    },
    outputs: [],
  };
  // input
  if (job.input.url.startsWith('astra')) {
    convertedJob.input.type = 'astra';
    convertedJob.input.url = Vue.util.extend({}, urlObject);
  } else {
    const newURLObj = processURLInfo({ url: job.input.url }, inputSupportedSchemes);
    convertedJob.input.url = Vue.util.extend({}, newURLObj);
    convertedJob.input.type = inputSchemeToType[convertedJob.input.url.selectedType];
  }
  // output
  for (let i = 0; i < job.outputs.length; i++) {
    convertedJob.outputs.push(JsonAPIOutputToNorm(job.outputs[i]));
  }
  return convertedJob;
}

export default {
  created() {
    if (this.$route.params.id) {
      this.isEditing = true;
      const jobId = this.$route.params.id;
      this.form.id = jobId;
      this.$store.commit('setPageLoader');
      this.$store.dispatch('jobs/getJobById', jobId)
        .then((job) => {
          const j = JsonAPIJobToNorm(job);
          this.savedJob = JSON.parse(JSON.stringify(j));
          this.form = Vue.util.extend({}, j);
          this.$store.dispatch('encodingProfiles/getProfiles')
            .then((profiles) => {
              this.profiles = profiles;
            })
            .catch((errorMsg) => {
              this.$toasted.error(errorMsg, {
                theme: 'toasted-primary',
                position: 'top-center',
                duration: 10000,
              });
            });
        })
        .catch((err) => {
          console.log(err);
          // this.$router.push({ name: 'LiveTranscodingDashboard' });
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    } else {
      this.form.input.url = Vue.util.extend({}, urlObject);
      const output = Vue.util.extend({}, outputObject);
      output.url = Vue.util.extend({}, urlObject);
      output.url.selectedType = 'http';
      this.form.outputs.push(output);
      this.$store.commit('setPageLoader');
      this.$store.dispatch('encodingProfiles/getProfiles')
        .then((profiles) => {
          this.profiles = profiles;
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 10000,
          });
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    }
  },
  data() {
    return {
      isSaving: false,
      isEditing: false,
      profileInfoDialog: false,
      showAdvancedSettings: false,
      form: {
        id: 0,
        name: '',
        profile: '',
        input: {
          url: {}, // urlObject
          type: 'rtmp pull',
          pn: '0',
          audio_pid: '0',
          video_pid: '0',
          data_pid: '0',
        },
        outputs: [], // array of outputObject
      },
      savedJob: {},
      rules: {
        nameRules: [
          v => !!v || this.text('title_req_err'),
          v => (v.length >= 3 && v.length <= 255) || this.text('title_min_err'),
        ],
        segmentSize: [
          v => (v >= 1 && v <= 60) || this.text('segment_size_err'),
        ],
      },
      formats: LIVE_ENCODING_FORMATS,
      inputTypes: ['rtmp pull', 'rtmp push', 'http', 'http+hls', 'astra', 'srt'],
      outputTypes: ['origin', 'webdav'],
      profiles: [],
      hlsVersions: [3, 5],
      videoCodecMap: VIDEO_CODEC_MAP,
      audioChannelsMap: AUDIO_CHANNELS_MAP,
      h264ProfileMap: H264_CODEC_PROFILE_MAP,
      h265ProfileMap: H265_CODEC_PROFILE_MAP,
      resizeModeMap: RESIZE_MODE_MAP,
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    selectedProfile() {
      for (let i = 0; i < this.profiles.length; i++) {
        if (this.profiles[i].id === this.form.profile) {
          return this.profiles[i];
        }
      }
      return {};
    },
    isJobValid() {
      if (!(this.form.name.length >= 3 && this.form.name.length <= 255) || this.form.profile === '') {
        return false;
      }
      // input
      if (!['rtmp push', 'astra'].includes(this.form.input.type) && this.form.input.url.hostPath === '') {
        return false;
      }
      // output
      let noOutput = true;
      for (let i = 0; i < this.form.outputs.length; i++) {
        const output = this.form.outputs[i];
        const { url } = output;
        if (output.type === 'webdav' && url.hostPath === '') {
          return false;
        }
        if (output.format.startsWith('segmented') && !(output.segmentSize >= 1 && output.segmentSize <= 60)) {
          return false;
        }
        noOutput = false;
      }
      return !noOutput;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    onInputTypeChange() {
      this.form.input.url.selectedType = inputTypeToScheme[this.form.input.type];
      this.buildInputURL();
    },
    buildInputURL() {
      this.form.input.url.url = buildURL(this.form.input.url);
    },
    onInputURLChange() {
      const newURLObj = processURLInfo(this.form.input.url, inputSupportedSchemes);
      this.form.input.url = Vue.util.extend({}, newURLObj);
      this.form.input.type = inputSchemeToType[this.form.input.url.selectedType];
    },
    changeInputSecure() {
      this.form.input.url.secure = !this.form.input.url.secure;
      this.buildInputURL();
    },
    outputFormats(i) {
      if (this.form.outputs[i].type === 'webdav') {
        return this.formats.filter(format => format.extId !== 'rtmp');
      }
      return this.formats;
    },
    addOutput() {
      const output = Vue.util.extend({}, outputObject);
      output.url = Vue.util.extend({}, urlObject);
      output.url.selectedType = 'http';
      this.form.outputs.push(Vue.util.extend({}, output));
    },
    removeOutput(i) {
      this.form.outputs.splice(i, 1);
    },
    onOutputTypeChange(i) {
      this.form.outputs[i].url.selectedType = outputTypeToScheme[this.form.outputs[i].type];
      this.buildOutputUrl(i);
      if (this.form.outputs[i].type === 'webdav' && this.form.outputs[i].format === 'rtmp') {
        this.form.outputs[i].format = 'segmented_hls';
      }
    },
    buildOutputUrl(i) {
      this.form.outputs[i].url.url = buildURL(this.form.outputs[i].url);
    },
    onOutputURLChange(i) {
      const newURLObj = processURLInfo(this.form.outputs[i].url, outputSupportedSchemes, 'http');
      this.form.outputs[i].url = Vue.util.extend({}, newURLObj);
    },
    changeOutputSecure(i) {
      this.form.outputs[i].url.secure = !this.form.outputs[i].url.secure;
      this.buildOutputUrl(i);
    },
    saveJob() {
      this.isSaving = true;
      if (!this.isJobValid) return;

      let action = 'jobs/createJob';
      if (this.isEditing) {
        this.form.outputs = this.compareOutputs();
        action = 'jobs/patchJob';
      }
      const job = new NormJobToJsonAPI(this.form);
      this.$store.dispatch(action, job)
        .then((result) => {
          this.$router.push({
            name: 'LiveTranscodingJobDetailPage',
            params: {
              id: result.id,
            },
          });
        })
        .catch((errorMsg) => {
          this.isSaving = false;
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    compareOutputs() {
      const sourceOutputMap = {};
      if (this.savedJob.outputs) {
        for (let i = 0; i < this.savedJob.outputs.length; i += 1) {
          const o = this.savedJob.outputs[i];
          const key = o.format + o.hlsVersion + o.segmentSize + o.type + o.url.url;
          sourceOutputMap[o.id] = key;
        }
      }

      const newOutputs = [];
      for (let i = 0; i < this.form.outputs.length; i += 1) {
        const o = this.form.outputs[i];
        const key = o.format + o.hlsVersion + o.segmentSize + o.type + o.url.url;
        // eslint-disable-next-line no-prototype-builtins
        if (sourceOutputMap.hasOwnProperty(o.id) && sourceOutputMap[o.id] !== key) {
          o.id = 0;
          newOutputs.push(o);
        } else {
          newOutputs.push(o);
        }
      }
      return newOutputs;
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
